import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search } from 'lucide-react';

const normalizeText = (value) => String(value ?? '').trim().toLowerCase();

const SearchableProductInput = ({
  value,
  products = [],
  placeholder = 'Search product',
  loading = false,
  onChangeText,
  onSelectProduct,
  menuPlacement = 'bottom',
  className = '',
  inputClassName = '',
}) => {
  const containerRef = useRef(null);
  const closeTimerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      if (closeTimerRef.current) {
        window.clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  const filteredProducts = useMemo(() => {
    const query = normalizeText(value);
    const ranked = products.filter((product) => {
      if (!query) return true;
      return [
        product.name,
        product.sku,
        product.label,
      ].some((field) => normalizeText(field).includes(query));
    });

    return ranked.slice(0, 8);
  }, [products, value]);

  const menuClassName = menuPlacement === 'top'
    ? 'absolute bottom-[calc(100%+0.45rem)] left-0 right-0 z-[90] overflow-hidden rounded-lg border border-slate-200 bg-white shadow-[0_24px_48px_-24px_rgba(15,23,42,0.45)]'
    : 'absolute left-0 right-0 top-[calc(100%+0.45rem)] z-[90] overflow-hidden rounded-lg border border-slate-200 bg-white shadow-[0_24px_48px_-24px_rgba(15,23,42,0.45)]';

  const scheduleClose = () => {
    closeTimerRef.current = window.setTimeout(() => {
      setIsOpen(false);
    }, 120);
  };

  const cancelScheduledClose = () => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <Search className="pointer-events-none absolute left-3 top-1/2 z-[1] h-4 w-4 -translate-y-1/2 text-slate-400" />
      <input
        className={`input rounded-lg pl-10 ${inputClassName}`}
        type="text"
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        onFocus={() => {
          cancelScheduledClose();
          setIsOpen(true);
        }}
        onBlur={scheduleClose}
        onChange={(event) => {
          onChangeText(event.target.value);
          setIsOpen(true);
        }}
      />

      {isOpen ? (
        <div className={menuClassName}>
          <div className="max-h-64 overflow-y-auto py-2">
            {loading ? (
              <div className="px-4 py-3 text-sm text-slate-500">Loading products...</div>
            ) : filteredProducts.length ? (
              filteredProducts.map((product) => (
                <button
                  key={product.key || product.productKey || product.id || `${product.name}-${product.sku || 'nosku'}`}
                  type="button"
                  className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition hover:bg-slate-50"
                  onMouseDown={(event) => {
                    event.preventDefault();
                    cancelScheduledClose();
                    onSelectProduct(product);
                    setIsOpen(false);
                  }}
                >
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-slate-900">{product.name || product.label}</div>
                    <div className="mt-0.5 truncate text-xs text-slate-500">{product.sku || 'No SKU'}</div>
                  </div>
                  {product.sku ? (
                    <span className="shrink-0 rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600">
                      {product.sku}
                    </span>
                  ) : null}
                </button>
              ))
            ) : (
              <div className="px-4 py-3 text-sm text-slate-500">No products found.</div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default SearchableProductInput;
